import { CheckOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { theme, Segmented, Switch, Tooltip } from 'antd';

// Tri-state option set (only rendered when `allowNull` is set). Segmented values must be
// strings, so we map to/from the real null / true / false. Same idiom as the is_paid /
// is_issued filters in Layouts/HeaderFilters.js.
const TRISTATE_OPTIONS = [
  {
    value: 'null',
    label: (
      <Tooltip title="Vrednost ostane nespremenjena">
        <MinusOutlined />
      </Tooltip>
    ),
  },
  { value: 'true', label: <CheckOutlined /> },
  { value: 'false', label: <CloseOutlined /> },
];

const valueToSeg = (v) => (v == null ? 'null' : v ? 'true' : 'false');
const segToValue = (seg) => (seg === 'null' ? null : seg === 'true');

const TRISTATE_CAPTIONS = {
  null: 'Brez sprememb',
  true: 'Nastavi na "Da"',
  false: 'Nastavi na "Ne"',
};

// A larger, card-style boolean toggle. The whole card is clickable and the
// active state is tinted with the success color. Theme-token based so it
// works in both light and dark mode.
//
// With `allowNull`, the card becomes tri-state: null means "leave the value alone"
// (used by mass edit, where no change must be distinguishable from setting false).
const BoolToggle = ({ value, onChange, label, disabled, allowNull = false }) => {
  const { token } = theme.useToken();
  const active = !!value;
  const isNull = allowNull && value == null;

  const colors = isNull
    ? { border: token.colorBorder, background: token.colorFillQuaternary, color: token.colorTextTertiary }
    : active
      ? { border: token.colorSuccess, background: token.colorSuccessBg, color: token.colorSuccess }
      : { border: token.colorError, background: token.colorErrorBg, color: token.colorError };

  return (
    <div
      onClick={() => !disabled && !allowNull && onChange?.(!active)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        minHeight: 44,
        padding: allowNull ? '6px 14px' : '0 14px',
        height: allowNull ? undefined : 44,
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${colors.border}`,
        background: colors.background,
        color: colors.color,
        cursor: disabled ? 'not-allowed' : allowNull ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        userSelect: 'none',
        transition: 'border-color .2s ease, background-color .2s ease, color .2s ease',
      }}
    >
      {allowNull ? (
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>{label}</span>
          <small style={{ opacity: 0.85 }}>{TRISTATE_CAPTIONS[valueToSeg(value)]}</small>
        </span>
      ) : (
        <span style={{ fontSize: 15, fontWeight: 500 }}>{label}</span>
      )}
      <span onClick={(e) => e.stopPropagation()}>
        {allowNull ? (
          <Segmented
            size="small"
            disabled={disabled}
            value={valueToSeg(value)}
            options={TRISTATE_OPTIONS}
            onChange={(seg) => onChange?.(segToValue(seg))}
          />
        ) : (
          <Switch checked={active} disabled={disabled} onChange={(checked) => onChange?.(checked)} />
        )}
      </span>
    </div>
  );
};

export default BoolToggle;
